import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { PublicLayout } from "@/components/PublicLayout";
import { useAdminAuth } from "@/context/AdminAuth";

export default function AdminLogin() {
    const [passcode, setPasscode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const { login, verified } = useAdminAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (verified) navigate("/admin/dashboard", { replace: true });
    }, [verified, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(passcode);
            toast.success("Signed in");
            navigate("/admin/dashboard");
        } catch (err) {
            toast.error("Invalid passcode");
        } finally {
            setSubmitting(false);
        }
    };

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const signInWithGoogle = () => {
        const redirectUrl = window.location.origin + "/admin/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <PublicLayout>
            <section className="max-w-md mx-auto px-6 py-24" data-testid="admin-login-page">
                <div className="card-clay p-8">
                    <div className="w-12 h-12 rounded-full bg-[hsl(var(--primary))] grid place-items-center text-white">
                        <Lock size={18} />
                    </div>
                    <h1 className="font-display text-3xl mt-6">Admin sign-in</h1>
                    <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">Sign in with Google or enter your admin passcode.</p>

                    <button
                        onClick={signInWithGoogle}
                        data-testid="google-signin-btn"
                        className="mt-6 w-full pill px-5 py-3 text-sm font-medium border border-[hsl(var(--border))] bg-white hover:bg-[hsl(var(--secondary))] transition-colors flex items-center justify-center gap-3"
                    >
                        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                        </svg>
                        Sign in with Google
                    </button>

                    <div className="my-6 flex items-center gap-3 text-xs text-[hsl(var(--muted-foreground))] uppercase font-mono">
                        <div className="h-px flex-1 bg-[hsl(var(--border))]" />
                        or
                        <div className="h-px flex-1 bg-[hsl(var(--border))]" />
                    </div>

                    <form onSubmit={submit} className="flex flex-col gap-4">
                        <input
                            type="password"
                            required
                            placeholder="Passcode"
                            value={passcode}
                            onChange={(e) => setPasscode(e.target.value)}
                            className="w-full bg-[hsl(var(--input))] border border-[hsl(var(--border))] rounded-md px-3 py-2 text-sm"
                            data-testid="admin-passcode-input"
                        />
                        <button
                            disabled={submitting}
                            className="pill btn-primary px-6 py-3 text-sm font-medium disabled:opacity-50"
                            data-testid="admin-login-submit"
                        >
                            {submitting ? "Signing in..." : "Sign in with passcode"}
                        </button>
                    </form>
                </div>
            </section>
        </PublicLayout>
    );
}
